import { useMemo } from 'react';
import { useMassMailContext } from '../context/MassMailContext';

export interface MissingEntityInfo {
  missingTypes: string[];
  missingParameters: string[];
  requiredEntityTypes: string[];
  requiredParameterNames: string[];
  hasEntityFields: boolean;
  hasParameterFields: boolean;
  missingCount: number;
}

export const useTemplateValidation = () => {
  const { state } = useMassMailContext();

  const entityTypeNames: { [key: string]: string } = {
    'building': 'Gebouw',
    'unit': 'Unit',
    'contact': 'Contact',
    'company': 'Bedrijf',
    'contract': 'Contract',
    'task': 'Taak',
    'helpdesk': 'Helpdesk'
  };

  const getMissingEntityTypes = useMemo((): MissingEntityInfo => {
    const content = state.emailSubject + ' ' + state.emailContent;
    
    // Check for missing entity types
    const entityPlaceholders = content.match(/\{[a-zA-Z]+\d+\.[a-zA-Z]+\}/g) || [];
    const requiredEntityTypes = new Set<string>();
    const addedEntityTypes = new Set<string>();
    
    // Extract required entity types from placeholders
    entityPlaceholders.forEach(placeholder => {
      const match = placeholder.match(/\{([a-zA-Z]+)\d+\./);
      if (match) {
        requiredEntityTypes.add(match[1]);
      }
    });
    
    // Track added entity types from mentioned entities
    state.mentionedEntities.forEach(entity => {
      addedEntityTypes.add(entity.type);
    });
    
    // Find missing entity types
    const missingEntityTypes = Array.from(requiredEntityTypes).filter(type => !addedEntityTypes.has(type));
    
    // Check for missing parameters (both gl_ and ind_ prefixed)
    const parameterPlaceholders = content.match(/\{(gl|ind)_[a-zA-Z0-9_]+\}/g) || [];
    const requiredParameterNames = new Set<string>();
    const addedParameterPlaceholders = new Set<string>();
    
    // Extract required parameter names
    parameterPlaceholders.forEach(placeholder => {
      requiredParameterNames.add(placeholder);
    });
    
    // Track added parameters
    state.parameters.forEach(param => {
      addedParameterPlaceholders.add(param.placeholder);
    });
    
    // Find missing parameters
    const missingParameterNames = Array.from(requiredParameterNames).filter(name => !addedParameterPlaceholders.has(name));
    
    return {
      missingTypes: missingEntityTypes.map(type => entityTypeNames[type] || type),
      missingParameters: Array.from(missingParameterNames),
      requiredEntityTypes: Array.from(requiredEntityTypes),
      requiredParameterNames: Array.from(requiredParameterNames),
      hasEntityFields: requiredEntityTypes.size > 0,
      hasParameterFields: requiredParameterNames.size > 0,
      missingCount: missingEntityTypes.length + missingParameterNames.length
    };
  }, [state.emailSubject, state.emailContent, state.mentionedEntities, state.parameters]);

  const hasUnfilledParameters = useMemo(() => {
    return state.parameters.some(param => {
      if (param.type === 'global') {
        return !(state.parameterValues[param.id] as string)?.trim();
      } else {
        const individualValues = state.parameterValues[param.id] as { [contactId: string]: string } || {};
        return state.contacts.some(contact => !individualValues[contact.id]?.trim());
      }
    });
  }, [state.parameters, state.parameterValues, state.contacts]);

  const isValid = useMemo(() => {
    const missingInfo = getMissingEntityTypes;
    return missingInfo.missingCount === 0 && !hasUnfilledParameters;
  }, [getMissingEntityTypes, hasUnfilledParameters]);

  return {
    getMissingEntityTypes,
    hasUnfilledParameters,
    isValid,
    entityTypeNames
  };
};
